import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { USER_ACTIVITY_INFO, type UserActivityActionCount } from '@ai-meeting/shared'
import { useUiStrings } from '@/hooks/useUiStrings'

interface ActivityPeopleDialogProps {
  /** The function whose people are being read. Mounted only while open. */
  row: UserActivityActionCount
  onClose: () => void
}

/**
 * Who pressed one function, and how many times each of them did.
 *
 * A dialog rather than another column: the People cell stays a count, and the
 * names only appear when somebody asks. Mounted only while open, the same way
 * the user activity drawer is, so closing it drops the list instead of hiding
 * it.
 */
export default function ActivityPeopleDialog({ row, onClose }: ActivityPeopleDialogProps) {
  const { t } = useUiStrings()
  const closeRef = useRef<HTMLButtonElement>(null)
  const label = USER_ACTIVITY_INFO[row.action].label

  useEffect(() => {
    closeRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/30 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-activity-people-title"
      data-testid="activity-people-dialog"
    >
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />

      <div className="relative flex max-h-[70vh] w-full max-w-md flex-col rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] shadow-2xl">
        <header className="flex shrink-0 items-start gap-3 border-b border-[var(--border-color)] px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2
              id="admin-activity-people-title"
              className="text-base font-bold text-[var(--text-primary)]"
            >
              {t.adminActivityPeopleTitle}
            </h2>
            <p className="mt-0.5 truncate text-xs text-[var(--text-secondary)]">{label}</p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg border border-[var(--border-color)] px-3 py-1.5 text-xs font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)]"
          >
            {t.close}
          </button>
        </header>

        <ul className="min-h-0 flex-1 overflow-y-auto px-5 py-2">
          {row.people.map((person) => (
            <li
              key={person.userId}
              className="flex items-center justify-between gap-3 border-b border-[var(--border-color)] py-2.5 last:border-0"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-[var(--text-primary)]">
                  {person.name}
                </p>
                {person.email ? (
                  <p className="truncate text-xs text-[var(--text-secondary)]">{person.email}</p>
                ) : null}
              </div>
              <span className="shrink-0 text-sm font-semibold tabular-nums text-[var(--text-primary)]">
                {person.count}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>,
    document.body
  )
}
